// Our own conversion events, fired into our own analytics.
//
// The script pair in index.html defines a synchronous `window.mag` stub
// before mag.js finishes downloading, so by the time any React handler
// runs the global is always there and an early call is queued + replayed
// rather than dropped. These wrappers exist for the two cases that are
// NOT that:
//
//   1. The tag never ran at all — vitest/jsdom mounts components without
//      index.html, so `window.mag` is genuinely undefined there. Every
//      one of these calls sits on a success path (a signup that worked, a
//      campaign that launched), and analytics must never be the reason
//      that path throws. Hence the optional call and the swallowed catch.
//   2. An ad-blocker ate mag.js. Same shape, same answer.
//
// The MagSdkApi type comes from the SDK package rather than a re-declared
// local interface, so `track()`'s signature here cannot drift from the
// one the tag actually ships — the package also carries the `window.mag`
// global declaration, which is why no `declare global` is needed below.

import type { MagSdkApi } from '@magig/analytics-sdk';

/** The live SDK, or undefined when the tag never loaded. Guarded on
 *  `window` too so this stays safe if a module ever gets imported from a
 *  non-browser context (a vitest node environment, a prerender). */
function sdk(): MagSdkApi | undefined {
  return typeof window === 'undefined' ? undefined : window.mag;
}

/** Record a conversion — a moment we would point an ad campaign at.
 *
 *  Only for explicit success-path moments: pageviews, clicks and scroll
 *  depth are already auto-captured, so re-tracking them by hand would
 *  double-count. Pass `value` on the payload whenever a real number is
 *  attached (plan price, pack price, committed daily budget) — Mag
 *  weights findings by value, so an untagged conversion reads as free. */
export function trackConversion(name: string, properties?: Record<string, unknown>): void {
  try {
    sdk()?.track(name, properties);
  } catch {
    // Deliberately silent: a broken tag must not surface as a founder-visible
    // error on a flow that otherwise succeeded.
  }
}

/** Attach the founder's user id to subsequent events, so a conversion can
 *  be described by WHO converted and not just that it happened. Traits are
 *  workspace-shaped (role, locale, org) — never the email or name. */
export function identifyFounder(userId: string, traits?: Record<string, unknown>): void {
  try {
    sdk()?.identify(userId, traits);
  } catch {
    // See trackConversion.
  }
}
